// ============================================================
// BATTLE UNIT & BASE
// ============================================================
//
// Session 6b: removed `wounded` field. Damage now subtracts directly
// from `totalSoldiers`; healing increases `totalSoldiers` (capped at
// the static `UCFG[type].hp` max). The `hp` getter is now an alias
// for `totalSoldiers` (kept for back-compat with read sites).

import { UCFG, type UnitModifier } from './constants.ts';
import { nextUnitId } from './utils.ts';

export interface Pos {
  x: number;
  y: number;
}

export interface BattleUnit {
  id: string;
  team: number;
  type: string;
  totalSoldiers: number;
  pos: Pos;
  path: Pos[];
  pathIdx: number;
  // Visual interpolation
  vx: number;
  vy: number;
  flash: number;
  flashColor: string;
  /**
   * Flat attack bonus from Bless (Light) and similar effects.
   *
   * Session 10 (2026 follow-up): this is now a CACHED derived value
   * from the `modifiers` list. Source of truth is `modifiers`; spell.ts
   * `_pushModifier` + `_recomputeScalars` keeps this in sync. Combat.ts
   * still reads this field directly via the `atk` getter — no change
   * for downstream consumers. Lifecycle now driven by per-modifier
   * `ticksRemaining` decrements (see `UnitModifier.ticksRemaining`).
   */
  buffAtk: number;
  /**
   * Physical damage reduction fraction (0..1) — derived from `modifiers`
   * entries with kind='shieldPhys' (max wins).
   *
   * Session 10: cached derived scalar (was direct scalar). Combat.ts
   * reads this during Step C.
   */
  tickDefPhys: number;
  /**
   * Magical damage reduction fraction (0..1) — derived from `modifiers`
   * entries with kind='shieldMag' (max wins).
   *
   * Session 10: cached derived scalar. Spell.ts hostile-phase damage
   * reads this during Step D, terrainDot (tick.ts Step F) too.
   */
  tickDefMag: number;
  /**
   * Session 11 — cached "is this unit rooted right now?" derived from
   * `modifiers` entries with kind='rooted'. Any present rooted modifier
   * → `rooted = true`. Consumed by `movement.ts._collectIntents` to
   * skip Step B entirely (both sub-phases). Recomputed by
   * `_recomputeScalars` after every modifier push / expire.
   *
   * Distinct from a scalar number because rooted doesn't accumulate —
   * one rooted source is as binding as ten. The modifier list still
   * carries `ticksRemaining` so the lifecycle (end-of-tick decrement)
   * matches buffAtk / shield modifiers exactly.
   */
  rooted: boolean;
  /**
   * Session 11 (swamp terrain) — primed-to-skip flag for speed-1 units
   * (Infantry / Archer) on `'swamp'`. SEMANTIC:
   *   • `true`  at tick start + on swamp → SKIP this tick (budget=0),
   *     then flip to `false`. ("the skip was pending, we just cashed it")
   *   • `false` at tick start + on swamp → MOVE this tick (budget=1),
   *     then flip to `true`. ("we moved, so next swamp tick we skip")
   *   • Any non-swamp tick → reset to `true` so the next swamp encounter
   *     starts on the SKIP step (not a free first move).
   *
   * Initialized `true` on every fresh unit so a unit that ENTERS swamp
   * on its first tick of life still skips. Wire payloads default true.
   *
   * Not used by Cavalry (speed≥2): those get budget=1 unconditionally on
   * swamp (matches puddle's slowdown — no alternation).
   *
   * Lives directly on BattleUnit (not in `modifiers[]`) because it's
   * terrain-driven, not spell-driven — no `ticksRemaining` lifecycle.
   * Mutated by `movement.ts` at the start of every tick's budget pass.
   */
  swampSkipPending: boolean;
  /**
   * Session 11 follow-up — cached damage-taken multiplier derived from
   * `modifiers` entries with kind='defDown'. `dmgTakenMult = 1 + Σ(value)`.
   * No modifiers → 1.0 (no change). Each defDown of value 0.05 contributes
   * +5% damage taken; multiple stack additively.
   *
   * Read by `applyDamageToUnit` (damage.ts) at the moment of HP subtraction
   * so EVERY damage source — Step C physical combat, Step D hostile spell
   * (incl. rainPuddle / Lightning / Fire), Step F burning-grass DOT — sees
   * the vulnerability. Application order: tickDef* reduces first (caller-
   * side), then dmgTakenMult amplifies the final ceil'd value.
   */
  dmgTakenMult: number;
  /**
   * Session 10 — active per-spell modifiers (source of truth for
   * `buffAtk` / `tickDefPhys` / `tickDefMag` / `rooted`). See `UnitModifier`
   * doc in constants.ts for the lifecycle / stacking rules.
   *
   * Push via `_pushModifier` in spell.ts; decrement + expire in
   * `tick.ts` end-of-tick; both paths call `_recomputeScalars(u)` so
   * the cached scalars above match this list at all times.
   */
  modifiers: UnitModifier[];
  dead: boolean;
  // Computed
  readonly hp: number;
  readonly atk: number;
}

/** Base structure — occupies multiple tiles, can be attacked */
export interface Base {
  team: number;
  tiles: Pos[];
  maxHp: number;
  hp: number;
  destroyed: boolean;
}

export function mkUnit(
  type: string,
  team: number,
  x: number,
  y: number,
  soldiers?: number,
  path?: Pos[],
): BattleUnit {
  const c = UCFG[type];
  const unit: BattleUnit = {
    id: nextUnitId(),
    team,
    type,
    totalSoldiers: soldiers ?? c.hp,
    pos: { x, y },
    path: path ?? [],
    pathIdx: 0,
    vx: x,
    vy: y,
    flash: 0,
    flashColor: '#fff',
    buffAtk: 0,
    tickDefPhys: 0,
    tickDefMag: 0,
    rooted: false,
    // Primed `true` so a unit's first-ever swamp tick skips, not moves
    // (see swampSkipPending field doc + movement.ts swamp budget logic).
    swampSkipPending: true,
    dmgTakenMult: 1,
    modifiers: [],
    dead: false,
    get hp() { return this.totalSoldiers; },
    get atk() { return this.hp * c.atk + this.buffAtk; },
  };
  return unit;
}

export function mkBase(team: number, tiles: Pos[], hp: number): Base {
  return { team, tiles: tiles.map(t => ({ ...t })), maxHp: hp, hp, destroyed: false };
}

/**
 * Reconstruct a BattleUnit from a plain JSON object received over the
 * network. JSON round-trip strips the `hp` / `atk` getters, so we rebuild
 * them with the same semantics as `mkUnit`. Phase 3d Step 4 — used by
 * the battleReady push event handler.
 *
 * Session 6b: ignore legacy `wounded` field if present in the payload —
 * old saved deployments / replay logs may still carry it.
 */
export function unitFromJson(u: any): BattleUnit {
  const c = UCFG[u.type];
  if (!c) throw new Error('unitFromJson: unknown type "' + u.type + '"');
  const unit: BattleUnit = {
    id: u.id,
    team: u.team,
    type: u.type,
    totalSoldiers: u.totalSoldiers ?? c.hp,
    pos: { x: u.pos.x, y: u.pos.y },
    path: Array.isArray(u.path) ? u.path.map((p: Pos) => ({ x: p.x, y: p.y })) : [],
    pathIdx: u.pathIdx ?? 0,
    vx: u.vx ?? u.pos.x,
    vy: u.vy ?? u.pos.y,
    flash: u.flash ?? 0,
    flashColor: u.flashColor ?? '#fff',
    buffAtk: u.buffAtk ?? 0,
    tickDefPhys: u.tickDefPhys ?? 0,
    tickDefMag: u.tickDefMag ?? 0,
    rooted: !!u.rooted,
    // Default `true` if wire payload omits the field — matches mkUnit's
    // primed default (see swampSkipPending field doc). Explicit `false`
    // is honoured (e.g. mid-swamp state on a hot-resumed session).
    swampSkipPending: u.swampSkipPending !== false,
    dmgTakenMult: typeof u.dmgTakenMult === 'number' && u.dmgTakenMult > 0 ? u.dmgTakenMult : 1,
    // Session 10 — sanitize incoming modifier list shape. Wire format
    // doesn't currently ship modifiers (battleReady sends units at
    // initial state, mid-battle state is server-only), but tolerate
    // either an array or missing field for forward compat.
    modifiers: Array.isArray(u.modifiers)
      ? u.modifiers.map((m: any) => ({
          kind: m.kind,
          value: typeof m.value === 'number' ? m.value : 0,
          ticksRemaining: typeof m.ticksRemaining === 'number' ? m.ticksRemaining : 1,
          // Default startingTicks = ticksRemaining if missing (legacy wire payloads).
          startingTicks: typeof m.startingTicks === 'number'
            ? m.startingTicks
            : (typeof m.ticksRemaining === 'number' ? m.ticksRemaining : 1),
          source: typeof m.source === 'number' ? m.source : 0,
        }))
      : [],
    dead: u.dead ?? false,
    get hp() { return this.totalSoldiers; },
    get atk() { return this.hp * c.atk + this.buffAtk; },
  };
  return unit;
}

/** Same idea as unitFromJson, but for Base. */
export function baseFromJson(b: any): Base {
  return {
    team: b.team,
    tiles: Array.isArray(b.tiles) ? b.tiles.map((t: Pos) => ({ x: t.x, y: t.y })) : [],
    maxHp: b.maxHp ?? b.hp ?? 0,
    hp: b.hp ?? 0,
    destroyed: !!b.destroyed,
  };
}
